import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { SectionCard } from './SectionCard';
import { Plus, Trash2, Save, Loader2 } from 'lucide-react';

export default function CompanyManager({ onSaved, toast }) {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { try { const r = await api.get('/admin/company'); setData(r.data || {}); } catch { /* ignore */ } })(); }, []);

  if (!data) return <SectionCard title="Company Info"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7941e]" /></SectionCard>;

  const save = async () => {
    try {
      const payload = { ...data };
      delete payload._id; delete payload._singleton;
      payload.phones = (payload.phones || []).flatMap(s => (s || '').split(/[\n,]/).map(x => x.trim()).filter(Boolean));
      payload.emails = (payload.emails || []).flatMap(s => (s || '').split(/[\n,]/).map(x => x.trim()).filter(Boolean));
      payload.industries = (payload.industries || []).flatMap(s => (s || '').split(/[\n,]/).map(x => x.trim()).filter(Boolean));
      await api.put('/admin/company', payload);
      toast({ title: 'Company info saved' });
      onSaved && onSaved();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail, variant: 'destructive' });
    }
  };

  const updateContact = (idx, key, val) => {
    const list = [...(data.contacts || [])];
    list[idx] = { ...list[idx], [key]: val };
    setData({ ...data, contacts: list });
  };

  return (
    <SectionCard title="Company Info" subtitle="Main business details shown across site" action={<button data-testid="save-company-btn" onClick={save} className="btn-brand text-sm"><Save className="w-4 h-4" /> Save</button>}>
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Company Name</Label><Input value={data.name || ''} onChange={e => setData({ ...data, name: e.target.value })} /></div>
          <div><Label>Tagline</Label><Input value={data.tagline || ''} onChange={e => setData({ ...data, tagline: e.target.value })} /></div>
        </div>
        <div><Label>Established / Certification line</Label><Input value={data.established || ''} onChange={e => setData({ ...data, established: e.target.value })} /></div>
        <div><Label>Address</Label><Textarea rows={2} value={data.address || ''} onChange={e => setData({ ...data, address: e.target.value })} /></div>

        <div>
          <Label>Phone Numbers (one per line)</Label>
          <Textarea rows={2} value={(data.phones || []).join('\n')} onChange={e => setData({ ...data, phones: e.target.value.split('\n') })} />
        </div>
        <div>
          <Label>Email Addresses (one per line)</Label>
          <Textarea rows={2} value={(data.emails || []).join('\n')} onChange={e => setData({ ...data, emails: e.target.value.split('\n') })} />
        </div>

        <div>
          <Label>Contact Persons</Label>
          <div className="space-y-2 mt-1">
            {(data.contacts || []).map((c, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center">
                <Input placeholder="Name" value={c.name} onChange={e => updateContact(i, 'name', e.target.value)} />
                <Input placeholder="Phone" value={c.phone} onChange={e => updateContact(i, 'phone', e.target.value)} />
                <div className="flex gap-2">
                  <Input placeholder="Role" value={c.role || ''} onChange={e => updateContact(i, 'role', e.target.value)} />
                  <button onClick={() => setData({ ...data, contacts: data.contacts.filter((_, x) => x !== i) })} className="p-2 text-red-600 hover:bg-red-50 rounded shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            <button onClick={() => setData({ ...data, contacts: [...(data.contacts || []), { name: '', phone: '', role: '' }] })} className="inline-flex items-center gap-1.5 text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded">
              <Plus className="w-4 h-4" /> Add contact
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>WhatsApp Number (digits only)</Label><Input value={data.whatsapp || ''} onChange={e => setData({ ...data, whatsapp: e.target.value })} /></div>
          <div><Label>YouTube Video ID</Label><Input value={data.youtube_id || ''} onChange={e => setData({ ...data, youtube_id: e.target.value })} /></div>
        </div>
        <div><Label>Business Hours</Label><Input value={data.business_hours || ''} onChange={e => setData({ ...data, business_hours: e.target.value })} /></div>

        <div>
          <Label>Industries <span className="text-gray-400 text-xs">(one per line OR comma-separated)</span></Label>
          <Textarea rows={4} value={(data.industries || []).join('\n')} onChange={e => setData({ ...data, industries: e.target.value.split('\n') })} />
        </div>

        <div>
          <Label>Social Links</Label>
          <div className="grid sm:grid-cols-2 gap-2 mt-1">
            {['facebook', 'linkedin', 'youtube', 'instagram'].map(k => (
              <Input key={k} placeholder={k} value={(data.socials || {})[k] || ''} onChange={e => setData({ ...data, socials: { ...(data.socials || {}), [k]: e.target.value } })} />
            ))}
          </div>
        </div>

        <button onClick={save} className="btn-brand"><Save className="w-4 h-4" /> Save Company Info</button>
      </div>
    </SectionCard>
  );
}
